import { call, put, select } from 'redux-saga/effects'
import {
  ICommandFacilityConfig,
  IConfigurationsState,
  IServiceConfig,
} from '../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import { throwPcrException } from '../../pcr/store/pcr.actions'
import { getPcrIdSelector } from '../../home/selectors'
import { getConfigurationsSelector } from '../../configurations/selectors'
import { IMergedDateTimeConfig, ITimesState } from '../store/times.types'
import { getTimesSelector } from '../selectors'
import { setTimes } from '../store/times.actions'
import { getCurrentDate, getCurrentTime } from '../../../utilities'
import { DateTimeConfig, DateTimeFieldsMap } from '../store/times.constants'
import { capitalizeFirstChar, setDataToObject } from '../../../utilities/data.util'
import { getServiceIdSelector } from '../../user/selectors'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import { IPcr } from '../../pcr/store/pcr.types'
import { removeSeconds } from '../../../utilities/dateTime.util'
import { addTimesItem } from '../../timeline/saga/addTimesItem.saga'
import { getPatientTimelineSelector } from '../../timeline/selectors/patientTimeline.selector'
import { ITimelineState } from '../../timeline/store/timeline.types'

export const getMergedDateTimeConfig = (
  serviceConfig: IServiceConfig,
  commandFacilityConfig?: ICommandFacilityConfig
): IMergedDateTimeConfig[] => {
  return DateTimeFieldsMap.map(({ fieldName, configName, disableConfigName }) => {
    const cmdVal = commandFacilityConfig?.[configName as keyof typeof commandFacilityConfig]
    const serviceVal = serviceConfig?.[configName as keyof typeof serviceConfig]
    const finalConfigValue =
      cmdVal != null ? (cmdVal === DateTimeConfig.USE_SERVICE_LEVEL_CONFIG ? serviceVal : cmdVal) : serviceVal

    const disableVal = serviceConfig?.[disableConfigName as keyof typeof serviceConfig]
    return {
      fieldName,
      configValue: typeof finalConfigValue === 'number' ? finalConfigValue : undefined,
      disableValue: typeof disableVal === 'number' ? disableVal : undefined,
    }
  })
}

const applyDateTimeConfigRules = (
  times: ITimesState,
  configs: Array<IMergedDateTimeConfig>,
  serviceConfig: IServiceConfig
): ITimesState => {
  let updated = { ...times }
  const displaySeconds = !!serviceConfig.DISPLAY_SECONDS

  const nowDate = getCurrentDate()
  const nowTime = displaySeconds ? getCurrentTime() : removeSeconds(getCurrentTime())

  const isCustomField = (fieldName: string) => fieldName.toLowerCase().startsWith('custom')
  const getCustomIndex = (fieldName: string) => fieldName.slice(6)

  const getFieldKeys = (fieldName: string) => {
    if (isCustomField(fieldName)) {
      const idx = getCustomIndex(fieldName)
      return {
        dateKey: `custom${idx}Date` as keyof ITimesState,
        timeKey: `custom${idx}Time` as keyof ITimesState,
        hideKey: `__hideCustom${idx}DateTime` as keyof ITimesState,
        disableKey: `__disableCustom${idx}DateTime` as keyof ITimesState,
        customLabel: serviceConfig[`CUSTOM_TIME${idx}_LABEL` as keyof IServiceConfig] as string | null,
        isCustom: true,
      }
    } else {
      return {
        dateKey: `${fieldName}Date` as keyof ITimesState,
        timeKey: `${fieldName}Time` as keyof ITimesState,
        hideKey: `__hide${capitalizeFirstChar(fieldName)}` as keyof ITimesState,
        disableKey: `__disable${capitalizeFirstChar(fieldName)}` as keyof ITimesState,
        customLabel: null,
        isCustom: false,
      }
    }
  }

  configs.forEach(({ fieldName, configValue, disableValue }) => {
    const { dateKey, timeKey, hideKey, disableKey, customLabel, isCustom } = getFieldKeys(fieldName)

    const hasCustomLabel = !isCustom || (customLabel != null && customLabel.trim() !== '')

    const canSetDefault =
      disableValue !== 1 && serviceConfig.DEFAULT_TIMES_UNUSED !== DateTimeConfig.UNSET_DEFAULT_VALUES && hasCustomLabel

    if (configValue === DateTimeConfig.SET_CURRENT_DATE_TIME_AS_DEFAULT && canSetDefault) {
      updated = setDataToObject(updated, dateKey, nowDate)
      updated = setDataToObject(updated, timeKey, nowTime)
    }

    updated = setDataToObject(updated, disableKey, disableValue === 1)

    const shouldHide =
      configValue === DateTimeConfig.HIDE_DATE_TIME || disableValue === 1 || (isCustom && !hasCustomLabel)

    updated = setDataToObject(updated, hideKey, shouldHide)
  })

  return updated
}

export function* setDateTimeConfig() {
  try {
    const pcrID: string = yield select(getPcrIdSelector)
    const times: ITimesState = yield select(getTimesSelector)
    const serviceId: string = yield select(getServiceIdSelector)

    const serviceConfig: IServiceConfig = yield select(getServiceConfigSelector)
    const configuration: IConfigurationsState = yield select(getConfigurationsSelector)
    const { commandFacilityConfig } = configuration

    if (pcrID) {
      const combinedCommandFacServiceConfig = getMergedDateTimeConfig(serviceConfig, commandFacilityConfig)
      const updatedTimes = applyDateTimeConfigRules(times, combinedCommandFacServiceConfig, serviceConfig)

      if (serviceConfig?.CUSTOM_TIME1 !== DateTimeConfig.HIDE_DATE_TIME) {
        updatedTimes.custom1DateTimeLabel = serviceConfig?.CUSTOM_TIME1_LABEL || ''
      }

      if (serviceConfig?.CUSTOM_TIME2 !== DateTimeConfig.HIDE_DATE_TIME) {
        updatedTimes.custom2DateTimeLabel = serviceConfig?.CUSTOM_TIME2_LABEL || ''
      }

      if (serviceConfig?.CUSTOM_TIME3 !== DateTimeConfig.HIDE_DATE_TIME) {
        updatedTimes.custom3DateTimeLabel = serviceConfig?.CUSTOM_TIME3_LABEL || ''
      }

      if (serviceConfig?.CUSTOM_TIME4 !== DateTimeConfig.HIDE_DATE_TIME) {
        updatedTimes.custom4DateTimeLabel = serviceConfig?.CUSTOM_TIME4_LABEL || ''
      }
      yield put(setTimes(updatedTimes))

      const pcrDocsService = new PcrDocsService(serviceId)
      const pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrID)

      yield call(addTimesItem, true)
      const timeline: ITimelineState = yield select(getPatientTimelineSelector)

      const pcrDocUpdated = {
        ...pcrDoc,
        times: updatedTimes,
        patient: { ...pcrDoc.patient, timelineItems: timeline.items },
      }
      yield call([pcrDocsService, pcrDocsService.savePCR], pcrDocUpdated)
    }
  } catch (e) {
    yield put(throwPcrException(`setDateTimeConfig() error: ${e}`))
  }
}
